import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiX, 
  FiDollarSign, 
  FiArrowUp, 
  FiArrowDown, 
  FiTrendingUp, 
  FiCreditCard, 
  FiActivity, 
  FiBarChart2,
  FiCheckCircle 
} from '../utils/icons';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { useUserData } from '../utils/UserDataContext';
import Modal from '../components/modals/Modal';
import FinanceForm from '../components/forms/FinanceForm';

const Finances: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, addTransaction, updateTransaction, deleteTransaction,
          addBill, updateBill, deleteBill, markBillAsPaid,
          addAsset, updateAsset, deleteAsset,
          addIncome, updateIncome, deleteIncome,
          addSavingsGoal, updateSavingsGoal, deleteSavingsGoal, updateSavingsGoalProgress } = useUserData();

  // Get the active tab from URL query parameter or default to 'transactions'
  const queryParams = new URLSearchParams(location.search);
  const defaultTab = queryParams.get('tab') || 'transactions';
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Modal states
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [isIncomeModalOpen, setIsIncomeModalOpen] = useState(false);
  const [isSavingsGoalModalOpen, setIsSavingsGoalModalOpen] = useState(false);
  
  // Edit/Delete states
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{id: string, type: string} | null>(null);

  // Update URL when tab changes
  useEffect(() => {
    navigate(`/finances?tab=${activeTab}`, { replace: true });
  }, [activeTab, navigate]);

  // Handle tab change
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  // Generic function to open edit modal
  const handleEdit = (item: any, type: string) => {
    setEditingItem(item);
    switch(type) {
      case 'transaction':
        setIsTransactionModalOpen(true);
        break;
      case 'bill':
        setIsBillModalOpen(true);
        break;
      case 'asset':
        setIsAssetModalOpen(true);
        break;
      case 'income':
        setIsIncomeModalOpen(true);
        break;
      case 'savingsGoal':
        setIsSavingsGoalModalOpen(true);
        break;
      default:
        break;
    }
  };

  // Generic function to handle delete confirmation
  const handleDeleteConfirm = (id: string, type: string) => {
    setItemToDelete({ id, type });
    setIsDeleteModalOpen(true);
  };

  // Perform the actual delete operation
  const confirmDelete = () => {
    if (!itemToDelete) return;

    switch(itemToDelete.type) {
      case 'transaction':
        deleteTransaction(itemToDelete.id);
        break;
      case 'bill':
        deleteBill(itemToDelete.id);
        break;
      case 'asset':
        deleteAsset(itemToDelete.id);
        break;
      case 'income':
        deleteIncome(itemToDelete.id);
        break;
      case 'savingsGoal':
        deleteSavingsGoal(itemToDelete.id);
        break;
      default:
        break;
    }

    setIsDeleteModalOpen(false);
    setItemToDelete(null);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background animate-fadeIn">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2">Finances</h1>
            <p className="text-text/70">Manage your financial data and track your progress</p>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-border">
            <div className="flex overflow-x-auto hide-scrollbar">
              <button
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  activeTab === 'transactions' ? 'border-primary text-primary' : 'border-transparent hover:text-primary/70'
                }`}
                onClick={() => handleTabChange('transactions')}
              >
                Transactions
              </button>
              <button
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  activeTab === 'bills' ? 'border-primary text-primary' : 'border-transparent hover:text-primary/70'
                }`}
                onClick={() => handleTabChange('bills')}
              >
                Bills
              </button>
              <button
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  activeTab === 'assets' ? 'border-primary text-primary' : 'border-transparent hover:text-primary/70'
                }`}
                onClick={() => handleTabChange('assets')}
              >
                Assets
              </button>
              <button
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  activeTab === 'income' ? 'border-primary text-primary' : 'border-transparent hover:text-primary/70'
                }`}
                onClick={() => handleTabChange('income')}
              >
                Income
              </button>
              <button
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  activeTab === 'goals' ? 'border-primary text-primary' : 'border-transparent hover:text-primary/70'
                }`}
                onClick={() => handleTabChange('goals')}
              >
                Savings Goals
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="pb-10">
            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Recent Transactions</h2>
                  <Button 
                    variant="primary" 
                    onClick={() => {
                      setEditingItem(null);
                      setIsTransactionModalOpen(true);
                    }}
                  >
                    <FiPlus className="mr-2" /> Add Transaction
                  </Button>
                </div>

                {/* Transaction list */}
                <Card variant="glass" className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-card-hover border-b border-border">
                          <th className="px-6 py-3 text-left text-sm font-medium">Merchant</th>
                          <th className="px-6 py-3 text-left text-sm font-medium">Category</th>
                          <th className="px-6 py-3 text-left text-sm font-medium">Date</th>
                          <th className="px-6 py-3 text-right text-sm font-medium">Amount</th>
                          <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userData.finances.transactions.map((transaction) => (
                          <tr key={transaction.id} className="border-b border-border last:border-0 hover:bg-card-hover transition-colors">
                            <td className="px-6 py-4 text-sm">{transaction.merchant}</td>
                            <td className="px-6 py-4 text-sm">
                              <span className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                                {transaction.category}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm">{transaction.date}</td>
                            <td className="px-6 py-4 text-sm text-right font-medium">
                              ${transaction.amount.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  className="p-1 rounded-full hover:bg-primary/10 transition-colors"
                                  onClick={() => handleEdit(transaction, 'transaction')}
                                >
                                  <FiEdit size={16} />
                                </button>
                                <button
                                  className="p-1 rounded-full hover:bg-red-500/10 text-red-500 transition-colors"
                                  onClick={() => handleDeleteConfirm(transaction.id, 'transaction')}
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        
                        {userData.finances.transactions.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-6 py-10 text-center text-text/50">
                              No transactions found. Add your first transaction!
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Transaction Modal */}
                <Modal
                  isOpen={isTransactionModalOpen}
                  onClose={() => {
                    setIsTransactionModalOpen(false);
                    setEditingItem(null);
                  }}
                  title={editingItem ? "Edit Transaction" : "Add New Transaction"}
                >
                  <FinanceForm
                    title={editingItem ? "Edit Transaction Details" : "Transaction Details"}
                    description="Record a transaction to track your spending."
                    fields={[
                      {
                        name: 'merchant',
                        label: 'Merchant',
                        type: 'text',
                        placeholder: 'e.g. Amazon, Starbucks',
                        required: true,
                        defaultValue: editingItem?.merchant || ''
                      },
                      {
                        name: 'amount',
                        label: 'Amount',
                        type: 'number',
                        placeholder: '0.00',
                        required: true,
                        defaultValue: editingItem?.amount || ''
                      },
                      {
                        name: 'date',
                        label: 'Date',
                        type: 'date',
                        required: true,
                        defaultValue: editingItem?.date || new Date().toISOString().split('T')[0]
                      },
                      {
                        name: 'category',
                        label: 'Category',
                        type: 'select',
                        required: true,
                        defaultValue: editingItem?.category || '',
                        options: [
                          { value: 'Shopping', label: 'Shopping' },
                          { value: 'Food & Drink', label: 'Food & Drink' },
                          { value: 'Transportation', label: 'Transportation' },
                          { value: 'Groceries', label: 'Groceries' },
                          { value: 'Entertainment', label: 'Entertainment' },
                          { value: 'Utilities', label: 'Utilities' },
                          { value: 'Housing', label: 'Housing' },
                          { value: 'Health', label: 'Health' },
                          { value: 'Education', label: 'Education' },
                          { value: 'Other', label: 'Other' }
                        ]
                      }
                    ]}
                    submitLabel={editingItem ? "Update" : "Add Transaction"}
                    onSubmit={(formData) => {
                      if (editingItem) {
                        updateTransaction(editingItem.id, {
                          merchant: formData.merchant,
                          amount: Number(formData.amount),
                          date: formData.date,
                          category: formData.category
                        });
                      } else {
                        addTransaction({
                          merchant: formData.merchant,
                          amount: Number(formData.amount),
                          date: formData.date,
                          category: formData.category
                        });
                      }
                      setIsTransactionModalOpen(false);
                      setEditingItem(null);
                    }}
                  />
                </Modal>
              </div>
            )}

            {/* Bills Tab */}
            {activeTab === 'bills' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Your Bills</h2>
                  <Button 
                    variant="primary" 
                    onClick={() => {
                      setEditingItem(null);
                      setIsBillModalOpen(true);
                    }}
                  >
                    <FiPlus className="mr-2" /> Add Bill
                  </Button>
                </div>

                {/* Bills list */}
                <Card variant="glass" className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-card-hover border-b border-border">
                          <th className="px-6 py-3 text-left text-sm font-medium">Name</th>
                          <th className="px-6 py-3 text-left text-sm font-medium">Due Date</th>
                          <th className="px-6 py-3 text-left text-sm font-medium">Frequency</th>
                          <th className="px-6 py-3 text-right text-sm font-medium">Amount</th>
                          <th className="px-6 py-3 text-center text-sm font-medium">Status</th>
                          <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userData.finances.bills.map((bill) => (
                          <tr key={bill.id} className="border-b border-border last:border-0 hover:bg-card-hover transition-colors">
                            <td className="px-6 py-4 text-sm font-medium">{bill.name}</td>
                            <td className="px-6 py-4 text-sm">{bill.dueDate}</td>
                            <td className="px-6 py-4 text-sm">
                              {bill.recurring ? (
                                <span className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                                  {bill.frequency || 'Monthly'}
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-full bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300 text-xs">
                                  One-time
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-right">
                              ${bill.amount.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 text-center">
                              {bill.paid ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-500/20 text-green-500 text-xs">
                                  <FiCheckCircle className="mr-1" /> Paid
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-accent/20 text-accent text-xs">
                                  Unpaid
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end space-x-2">
                                <button
                                  className={`p-1 rounded-full ${
                                    bill.paid 
                                      ? 'hover:bg-accent/10 text-accent' 
                                      : 'hover:bg-green-500/10 text-green-500'
                                  } transition-colors`}
                                  onClick={() => markBillAsPaid(bill.id, !bill.paid)}
                                  title={bill.paid ? "Mark as unpaid" : "Mark as paid"}
                                >
                                  {bill.paid ? <FiX size={16} /> : <FiCheckCircle size={16} />}
                                </button>
                                <button
                                  className="p-1 rounded-full hover:bg-primary/10 transition-colors"
                                  onClick={() => handleEdit(bill, 'bill')}
                                >
                                  <FiEdit size={16} />
                                </button>
                                <button
                                  className="p-1 rounded-full hover:bg-red-500/10 text-red-500 transition-colors"
                                  onClick={() => handleDeleteConfirm(bill.id, 'bill')}
                                >
                                  <FiTrash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        
                        {userData.finances.bills.length === 0 && (
                          <tr>
                            <td colSpan={6} className="px-6 py-10 text-center text-text/50">
                              No bills found. Add your first bill!
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Bill Modal */}
                <Modal
                  isOpen={isBillModalOpen}
                  onClose={() => {
                    setIsBillModalOpen(false);
                    setEditingItem(null);
                  }}
                  title={editingItem ? "Edit Bill" : "Add New Bill"}
                >
                  <FinanceForm
                    title={editingItem ? "Edit Bill Details" : "Bill Details"}
                    description="Add a recurring bill or one-time payment."
                    fields={[
                      {
                        name: 'name',
                        label: 'Bill Name',
                        type: 'text',
                        placeholder: 'e.g. Rent, Internet',
                        required: true,
                        defaultValue: editingItem?.name || ''
                      },
                      {
                        name: 'amount',
                        label: 'Amount',
                        type: 'number',
                        placeholder: '0.00',
                        required: true,
                        defaultValue: editingItem?.amount || ''
                      },
                      {
                        name: 'dueDate',
                        label: 'Due Date',
                        type: 'date',
                        required: true,
                        defaultValue: editingItem?.dueDate || ''
                      },
                      {
                        name: 'recurring',
                        label: 'Is Recurring?',
                        type: 'select',
                        required: true,
                        defaultValue: editingItem ? (editingItem.recurring ? 'true' : 'false') : 'true',
                        options: [
                          { value: 'true', label: 'Yes' },
                          { value: 'false', label: 'No' }
                        ]
                      },
                      {
                        name: 'frequency',
                        label: 'Frequency',
                        type: 'select',
                        defaultValue: editingItem?.frequency || 'monthly',
                        options: [
                          { value: 'monthly', label: 'Monthly' },
                          { value: 'quarterly', label: 'Quarterly' },
                          { value: 'yearly', label: 'Yearly' }
                        ]
                      }
                    ]}
                    submitLabel={editingItem ? "Update" : "Add Bill"}
                    onSubmit={(formData) => {
                      const billData = {
                        name: formData.name,
                        amount: Number(formData.amount),
                        dueDate: formData.dueDate,
                        recurring: formData.recurring === 'true',
                        frequency: formData.frequency || 'monthly'
                      };
                      
                      if (editingItem) {
                        updateBill(editingItem.id, billData);
                      } else {
                        addBill({
                          ...billData,
                          paid: false
                        });
                      }
                      setIsBillModalOpen(false);
                      setEditingItem(null);
                    }}
                  />
                </Modal>
              </div>
            )}

            {/* Assets Tab */}
            {/* ... will add in next edit ... */}
            
            {/* Delete Confirmation Modal */}
            <Modal
              isOpen={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              title="Confirm Delete"
              size="sm"
            >
              <div className="py-4">
                <p className="text-center mb-6">
                  Are you sure you want to delete this item? This action cannot be undone.
                </p>
                <div className="flex justify-center space-x-4">
                  <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" className="bg-red-500 hover:bg-red-600" onClick={confirmDelete}>
                    Delete
                  </Button>
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Finances; 